import { useEffect, useRef } from 'react'

import { supportedLanguages } from '@common'
import { LazyMotion, AnimatePresence, m } from 'framer-motion'

import { ExitIcon } from '@/components/Icons/ExitIcon'
import { useLocale } from '@/features/locale/useLocale'
import useOnClickOutside from '@/utils/useOnClickOutside'

import styles from './Menu.module.scss'

type Props = React.PropsWithChildren<{
  isOpen: boolean
  closeMenu: () => void
}>

const CONTAINER_WIDTH = 308

const MenuContainer = ({ isOpen, closeMenu, children }: Props) => {
  const ref = useRef(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  useOnClickOutside(ref, closeMenu)
  const locale = useLocale()
  const features = () => import('@/animations/features').then((a) => a.default)
  const isRTL = supportedLanguages[locale].rightToLeft

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove(styles.BodyStyles)
    } else {
      document.body.classList.add(styles.BodyStyles)
      closeButtonRef.current?.focus()
    }
    return () => document.body.classList.remove(styles.BodyStyles)
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <LazyMotion features={features}>
          <m.div
            key="menu-container"
            className={styles.Container}
            initial={{ x: isRTL ? -CONTAINER_WIDTH : CONTAINER_WIDTH }}
            animate={{ x: 0 }}
            exit={{ x: isRTL ? -CONTAINER_WIDTH : CONTAINER_WIDTH }}
            transition={{ duration: 0.2 }}
          >
            <div className={styles.Header}>
              <button className={styles.MenuButton} onClick={closeMenu} ref={closeButtonRef}>
                <ExitIcon />
              </button>
            </div>
            <div className={styles.Content}>{children}</div>
          </m.div>
          <m.div
            key="backdrop"
            className={styles.Backdrop}
            onClick={closeMenu}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          ></m.div>
        </LazyMotion>
      )}
    </AnimatePresence>
  )
}

export default MenuContainer
